import { type FormEvent, useCallback, useEffect, useState } from "react";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Select } from "../../components/Select";
import { ApiError, api } from "../../lib/api";
import type {
  GrantResponse,
  MemberResponse,
  MemberRole,
  NotificationChannel,
  StoreResponse,
} from "../../lib/api";
import styles from "./MembersPanel.module.css";

type MembersPanelProps = {
  storeId: string;
  store: StoreResponse | null;
  currentUserId: string | undefined;
  // Ownership transfer changes store.owner_id — the parent refetches the store.
  onOwnershipTransferred?: () => void;
};

const errorText = (e: unknown, fallback: string): string =>
  e instanceof ApiError ? (e.body?.error ?? e.message) : fallback;

export const MembersPanel = ({
  storeId,
  store,
  currentUserId,
  onOwnershipTransferred,
}: MembersPanelProps) => {
  const [members, setMembers] = useState<MemberResponse[]>([]);
  const [grants, setGrants] = useState<GrantResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<MemberRole>("viewer");
  const [busy, setBusy] = useState(false);
  // The admin row a pending ownership transfer targets, plus the password re-entry
  // (deliberate friction: this is the one action that can lock the caller out of
  // their own store settings).
  const [transferTarget, setTransferTarget] = useState<string | null>(null);
  const [transferPassword, setTransferPassword] = useState("");

  const load = useCallback(async () => {
    try {
      const fetched = await api.listMembers(storeId);
      setMembers(fetched);
      const isManager =
        store?.owner_id === currentUserId ||
        ["owner", "admin"].includes(fetched.find((m) => m.user_id === currentUserId)?.role ?? "");
      // Managers see every Grant; everyone else sees only their own (server enforces both).
      setGrants(isManager ? await api.listGrants(storeId) : await api.listMyGrants(storeId));
    } catch (e) {
      setError(errorText(e, "Couldn't load members."));
    } finally {
      setLoading(false);
    }
  }, [storeId, store?.owner_id, currentUserId]);

  useEffect(() => {
    void load();
  }, [load]);

  const self = members.find((m) => m.user_id === currentUserId);
  const canManage =
    store?.owner_id === currentUserId || self?.role === "owner" || self?.role === "admin";

  const handleAdd = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setBusy(true);
    setError(null);
    try {
      await api.addMember(storeId, { email: trimmed, role });
      setEmail("");
      setRole("viewer");
      await load();
    } catch (e) {
      setError(errorText(e, "Couldn't add that member."));
    } finally {
      setBusy(false);
    }
  };

  const handleRoleChange = async (userId: string, next: MemberRole): Promise<void> => {
    setError(null);
    try {
      await api.updateMemberRole(storeId, userId, { role: next });
      await load();
    } catch (e) {
      setError(errorText(e, "Couldn't change that role."));
    }
  };

  const handleRemove = async (userId: string): Promise<void> => {
    setError(null);
    try {
      await api.removeMember(storeId, userId);
      await load();
    } catch (e) {
      setError(errorText(e, "Couldn't remove that member."));
    }
  };

  const handleTransfer = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!transferTarget || !transferPassword) return;
    setBusy(true);
    setError(null);
    try {
      await api.transferOwnership(storeId, {
        new_owner_id: transferTarget,
        password: transferPassword,
      });
      setTransferTarget(null);
      setTransferPassword("");
      await load();
      onOwnershipTransferred?.();
    } catch (e) {
      setError(errorText(e, "Couldn't transfer ownership."));
    } finally {
      setBusy(false);
    }
  };

  const grantFor = (userId: string, channel: NotificationChannel) =>
    grants.find((grant) => grant.user_id === userId && grant.channel === channel);

  const handleGrantToggle = async (userId: string, channel: NotificationChannel): Promise<void> => {
    setError(null);
    try {
      if (grantFor(userId, channel)) {
        await api.deleteGrant(storeId, userId, channel);
      } else {
        await api.putGrant(storeId, userId, channel);
      }
      await load();
    } catch (e) {
      setError(errorText(e, "Couldn't change that notification grant."));
    }
  };

  const handleMyToggle = async (channel: NotificationChannel, enabled: boolean): Promise<void> => {
    setError(null);
    try {
      await api.setMyGrantEnabled(storeId, channel, enabled);
      await load();
    } catch (e) {
      setError(errorText(e, "Couldn't change your alert setting."));
    }
  };

  if (loading) return <div className={styles.status}>Loading…</div>;

  return (
    <div className={styles.panel}>
      {canManage && (
        <form className={styles.addRow} onSubmit={handleAdd} noValidate>
          <div className={styles.addField}>
            <label className={styles.label} htmlFor="member-email">
              Invite by email
            </label>
            <Input
              id="member-email"
              type="email"
              placeholder="teammate@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={busy}
              required
            />
          </div>
          <div className={styles.addField}>
            <label className={styles.label} htmlFor="member-role">
              Role
            </label>
            <Select
              id="member-role"
              value={role}
              onChange={(e) => setRole(e.target.value as MemberRole)}
              disabled={busy}
            >
              <option value="viewer">Viewer</option>
              <option value="admin">Admin</option>
            </Select>
          </div>
          <Button type="submit" disabled={busy}>
            Add member
          </Button>
        </form>
      )}

      {error && (
        <div role="alert" className={styles.error}>
          {error}
        </div>
      )}

      <ul className={styles.list}>
        {members.map((m) => {
          const isOwner = m.role === "owner";
          const manageable = canManage && !isOwner;
          return (
            <li key={m.user_id} className={styles.row}>
              <div className={styles.who}>
                <span className={styles.name}>{m.display_name ?? m.email}</span>
                <span className={styles.email}>{m.email}</span>
              </div>
              <div className={styles.grantCluster}>
                {(canManage || m.user_id === currentUserId) &&
                  (["sms", "telegram"] as const).map((channel) => {
                    const grant = grantFor(m.user_id, channel);
                    const isSelf = m.user_id === currentUserId;
                    const label = channel === "sms" ? "SMS" : "Telegram";
                    // One checkbox (2026-07-31): on your OWN row it means "I get alerts"
                    // (grant AND not paused; unchecking pauses, never revokes). On OTHER
                    // rows (managers) it means "they're allowed" (grant on/off).
                    const checked = isSelf ? (grant?.member_enabled ?? false) : grant !== undefined;
                    const disabled = isSelf && !canManage && grant === undefined;
                    const onChange = () => {
                      if (isSelf && grant) {
                        void handleMyToggle(channel, !grant.member_enabled);
                      } else if (isSelf && canManage && !grant) {
                        void handleGrantToggle(m.user_id, channel);
                      } else if (!isSelf) {
                        void handleGrantToggle(m.user_id, channel);
                      }
                    };
                    return (
                      <label key={channel} className={styles.grantBox} data-disabled={disabled}>
                        <input
                          type="checkbox"
                          aria-label={`${label} alerts for ${m.email}`}
                          checked={checked}
                          disabled={disabled}
                          onChange={onChange}
                        />
                        <span>{label}</span>
                        {!isSelf && grant && !grant.member_enabled && (
                          <span className={styles.grantHint}>(paused)</span>
                        )}
                      </label>
                    );
                  })}
                {m.user_id === currentUserId &&
                  !canManage &&
                  grants.every((grant) => grant.user_id !== m.user_id) && (
                    <span className={styles.grantHint}>Owner hasn't granted alerts</span>
                  )}
              </div>
              {manageable ? (
                <div className={styles.controls}>
                  <Select
                    aria-label={`Role for ${m.email}`}
                    value={m.role}
                    onChange={(e) => void handleRoleChange(m.user_id, e.target.value as MemberRole)}
                  >
                    <option value="viewer">Viewer</option>
                    <option value="admin">Admin</option>
                  </Select>
                  {store?.owner_id === currentUserId && m.role === "admin" && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={busy}
                      onClick={() => {
                        setTransferPassword("");
                        setTransferTarget(m.user_id);
                      }}
                    >
                      Make owner
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => void handleRemove(m.user_id)}>
                    Remove
                  </Button>
                </div>
              ) : (
                <span className={styles.roleTag} data-role={m.role}>
                  {m.role}
                </span>
              )}
              {transferTarget === m.user_id && (
                <form className={styles.transferConfirm} onSubmit={handleTransfer}>
                  <span className={styles.transferWarning}>
                    {m.display_name ?? m.email} will become the owner of this store; you will remain
                    an admin member. Confirm with your account password.
                  </span>
                  <Input
                    type="password"
                    aria-label="Account password"
                    placeholder="Your account password"
                    value={transferPassword}
                    onChange={(e) => setTransferPassword(e.target.value)}
                    disabled={busy}
                    required
                  />
                  <Button type="submit" size="sm" disabled={busy || !transferPassword}>
                    Transfer
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={() => setTransferTarget(null)}
                  >
                    Cancel
                  </Button>
                </form>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};
